import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { lazy, Suspense } from "react";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Critical path — eager load
import ThemeGlassV2 from "./pages/ThemeGlassV2";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Lazy load everything else
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const OTPVerifyPage = lazy(() => import("./pages/OTPVerifyPage"));
const UsernameSetupPage = lazy(() => import("./pages/UsernameSetupPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const AadhaarVerificationPage = lazy(() => import("./pages/AadhaarVerificationPage"));
const FeedPage = lazy(() => import("./pages/FeedPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const EditProfilePage = lazy(() => import("./pages/EditProfilePage"));
const JobsPage = lazy(() => import("./pages/JobsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const LiveStreamsPage = lazy(() => import("./pages/LiveStreamsPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const CreatePostPage = lazy(() => import("./pages/CreatePostPage"));
const AIGeneratorPage = lazy(() => import("./pages/AIGeneratorPage"));
const SchedulerPage = lazy(() => import("./pages/SchedulerPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const BuildsPage = lazy(() => import("./pages/BuildsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const InterviewPage = lazy(() => import("./pages/InterviewPage"));
const PagesListPage = lazy(() => import("./pages/PagesListPage"));
const PageDetailPage = lazy(() => import("./pages/PageDetailPage"));
const AINewsPage = lazy(() => import("./pages/AINewsPage"));
const GroupChatPage = lazy(() => import("./pages/GroupChatPage"));
const CommunityDetailPage = lazy(() => import("./pages/CommunityDetailPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const TutorialsPage = lazy(() => import("./pages/TutorialsPage"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const WorkspacePage = lazy(() => import("./pages/WorkspacePage"));
const WebinarPage = lazy(() => import("./pages/WebinarPage"));
const RecruiterPage = lazy(() => import("./pages/RecruiterPage"));
const AIHubPage = lazy(() => import("./pages/AIHubPage"));
const FounderHubPage = lazy(() => import("./pages/FounderHubPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AdminBlogListPage = lazy(() => import("./pages/AdminBlogPage").then(m => ({ default: m.AdminBlogListPage })));
const AdminBlogEditorPage = lazy(() => import("./pages/AdminBlogPage").then(m => ({ default: m.AdminBlogEditorPage })));
const CompleteProfilePage = lazy(() => import("./pages/CompleteProfilePage"));
const HashtagPage = lazy(() => import("./pages/HashtagPage"));
const WorkflowMarketPage = lazy(() => import("./pages/WorkflowMarketPage"));
const AdManagerPage = lazy(() => import("./pages/AdManagerPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const HackathonDetailPage = lazy(() => import("./pages/HackathonDetailPage"));
const SavedPostsPage = lazy(() => import("./pages/SavedPostsPage"));
const PostDetailPage = lazy(() => import("./pages/PostDetailPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const SuggestionPage = lazy(() => import("./pages/SuggestionPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const InvestorModulePage = lazy(() => import("./pages/InvestorModulePage"));
const HackathonsPage = lazy(() => import("./pages/HackathonsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsConditionsPage = lazy(() => import("./pages/TermsConditionsPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));
const MonetizationPage = lazy(() => import("./pages/MonetizationPage"));
import WelcomePopup from "./components/WelcomePopup";
import IncomingCallOverlay from "./components/IncomingCallOverlay";
import DemoAutoTour from "./components/DemoAutoTour";
import AIxTribeBotFAB from "./components/AIxTribeBotFAB";
import CelebrationListener from "./components/CelebrationListener";
import { GuestGateProvider } from "./components/GuestGate";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading, needsUsername, needsAadhaar, needsProfileCompletion, subscriptionActive } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="animate-pulse">
          <img 
            src="/brain-logo.png?v=4" 
            alt="AIxTribe" 
            className="w-20 h-20 object-contain rounded-lg"
          />
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  // Demo users bypass all redirects — they are read-only & short-lived
  const isDemoUser = user?.role === "demo" || user?.is_demo === true;
  if (isDemoUser) {
    return children;
  }

  if (needsUsername) {
    return <Navigate to="/setup-username" replace />;
  }

  if (needsAadhaar) {
    return <Navigate to="/verify-aadhaar" replace />;
  }

  // Skip mandatory onboarding wizard for admin roles so they can access dashboards immediately
  const isAdminRole = user?.role && ["admin", "developer", "moderator", "content_manager"].includes(user.role);

  if (needsProfileCompletion && !isAdminRole) {
    return <Navigate to="/complete-profile" replace />;
  }

  // If subscription expired, redirect to subscription page (skip for admins & demo)
  if (subscriptionActive === false && !isAdminRole && !isDemoUser) {
    return <Navigate to="/subscription" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/welcome" replace />;
  if (user.role !== "admin" && user.role !== "developer") return <Navigate to="/feed" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, needsUsername } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="animate-pulse">
          <img 
            src="/brain-logo.png?v=4" 
            alt="AIxTribe" 
            className="w-20 h-20 object-contain rounded-lg"
          />
        </div>
      </div>
    );
  }
  
  if (user && !needsUsername) {
    return <Navigate to="/feed" replace />;
  }
  
  return children;
};

const LazyFallback = () => (
  <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
    <div className="animate-pulse"><img src="/brain-logo.png?v=4" alt="AIxTribe" className="w-16 h-16 object-contain rounded-lg" style={{ mixBlendMode: "screen" }} /></div>
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      <Route path="/" element={<Navigate to="/welcome" replace />} />
      <Route path="/welcome" element={<PublicRoute><ThemeGlassV2 /></PublicRoute>} />
      <Route path="/preview-final" element={<ThemeGlassV2 />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/verify-otp" element={<OTPVerifyPage />} />
      <Route path="/setup-username" element={<UsernameSetupPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/verify-aadhaar" element={<AadhaarVerificationPage />} />
      <Route path="/complete-profile" element={<CompleteProfilePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsConditionsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      
      <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
      <Route path="/create-post" element={<ProtectedRoute><CreatePostPage /></ProtectedRoute>} />
      <Route path="/generator" element={<ProtectedRoute><AIGeneratorPage /></ProtectedRoute>} />
      <Route path="/scheduler" element={<ProtectedRoute><SchedulerPage /></ProtectedRoute>} />
      <Route path="/profile/:username" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/edit-profile" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
      <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
      <Route path="/jobs" element={<JobsPage />} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/chat/:userId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/chat/page/:pageId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/chat/group/:groupId" element={<ProtectedRoute><GroupChatPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/monetization" element={<ProtectedRoute><MonetizationPage /></ProtectedRoute>} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/builds" element={<ProtectedRoute><BuildsPage /></ProtectedRoute>} />
      <Route path="/live" element={<ProtectedRoute><LiveStreamsPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/interview/:roomCode" element={<ProtectedRoute><InterviewPage /></ProtectedRoute>} />
      <Route path="/pages" element={<ProtectedRoute><PagesListPage /></ProtectedRoute>} />
      <Route path="/page/:slug" element={<ProtectedRoute><PageDetailPage /></ProtectedRoute>} />
      <Route path="/community/:slug" element={<ProtectedRoute><CommunityDetailPage /></ProtectedRoute>} />
      <Route path="/news" element={<AINewsPage />} />
      <Route path="/ai-news" element={<AINewsPage />} />
      <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
      <Route path="/tutorials" element={<TutorialsPage />} />
      <Route path="/ai-tutorials" element={<TutorialsPage />} />
      <Route path="/quiz" element={<QuizPage />} />
      <Route path="/ai-quiz" element={<QuizPage />} />
      <Route path="/courses" element={<CoursesPage />} />
      <Route path="/workspace" element={<WorkspacePage />} />
      <Route path="/webinars" element={<WebinarPage />} />
      <Route path="/recruiter" element={<ProtectedRoute><RecruiterPage /></ProtectedRoute>} />
      <Route path="/ai-hub" element={<ProtectedRoute><AIHubPage /></ProtectedRoute>} />
      <Route path="/founder-hub" element={<ProtectedRoute><FounderHubPage /></ProtectedRoute>} />

      {/* SEO-friendly module URLs (primary, indexable) */}
      <Route path="/ai-tools" element={<WorkspacePage />} />
      <Route path="/ai-courses" element={<CoursesPage />} />
      <Route path="/ai-jobs" element={<JobsPage />} />
      <Route path="/ai-community" element={<SEOLandingPage slug="ai-community" />} />
      <Route path="/learn-ai" element={<SEOLandingPage slug="learn-ai" />} />

      {/* Blog — public, indexable */}
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />

      {/* Admin Blog Editor */}
      <Route path="/admin/blog" element={<ProtectedRoute><AdminBlogListPage /></ProtectedRoute>} />
      <Route path="/admin/blog/new" element={<ProtectedRoute><AdminBlogEditorPage /></ProtectedRoute>} />
      <Route path="/admin/blog/edit/:id" element={<ProtectedRoute><AdminBlogEditorPage /></ProtectedRoute>} />
      <Route path="/hashtag/:tag" element={<ProtectedRoute><HashtagPage /></ProtectedRoute>} />
      <Route path="/workflow-market" element={<ProtectedRoute><WorkflowMarketPage /></ProtectedRoute>} />
      <Route path="/ad-manager" element={<ProtectedRoute><AdManagerPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/hackathon/:id" element={<HackathonDetailPage />} />
      <Route path="/saved" element={<ProtectedRoute><SavedPostsPage /></ProtectedRoute>} />
      <Route path="/post/:postId" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
      <Route path="/investor-hub" element={<ProtectedRoute><InvestorModulePage /></ProtectedRoute>} />
      <Route path="/hackathons" element={<HackathonsPage />} />
      <Route path="/suggestions" element={<ProtectedRoute><SuggestionPage /></ProtectedRoute>} />

      {/* Public profile catch-all — MUST be last */}
      <Route path="/:username" element={<PublicProfilePage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ""}>
      <BrowserRouter>
        <AuthProvider>
          <GuestGateProvider>
            <AppRoutes />
            <WelcomePopup />
            <IncomingCallOverlay />
            <DemoAutoTour />
            <AIxTribeBotFAB />
            <CelebrationListener />
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: "#15151F",
                  border: "1px solid #2A2A3A",
                  color: "#FFFFFF",
                },
              }}
            />
          </GuestGateProvider>
      </AuthProvider>
    </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
